import React, { useEffect, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Task, TaskStatus, TaskPriority, TaskType, ChecklistItem } from '../types';
import { getChecklistItems, updateChecklistItem, getSubtasks, deleteTask } from '../services/taskService';
import { dvUpdate } from '../api/dataverseClient';
import { usePinState, getMyOid, PinKey } from './cardPinState';
import AutoSizingTextarea from '../components/AutoSizingTextarea';
import QuickCreatePunchModal from '../components/QuickCreatePunchModal';
import BoardModal, { BMCancelButton, BMDangerButton } from '../components/BoardModal';

// Priority color tokens — locked spec, April 19, 2026.
// Border stripe AND priority pill MUST share these tokens.
const PRIORITY_COLOR: Record<number, { bg: string; fg: string; label: string }> = {
  [TaskPriority.Critical]: { bg: '#DC2626', fg: '#FFFFFF', label: 'Critical' },
  [TaskPriority.High]:     { bg: '#E59F1C', fg: '#1C1917', label: 'High' },
  [TaskPriority.Medium]:   { bg: '#2563EB', fg: '#FFFFFF', label: 'Medium' },
  [TaskPriority.Low]:      { bg: '#6B7280', fg: '#FFFFFF', label: 'Low' },
};
const NULL_BORDER = '#DDE0E6';

const STATUS_BADGE: Record<number, { bg: string; fg: string; label: string }> = {
  [TaskStatus.Done]:       { bg: '#D1FAE5', fg: '#065F46', label: 'Done' },
  [TaskStatus.InProgress]: { bg: '#D4EEF5', fg: '#0A5568', label: 'In Progress' },
  [TaskStatus.InReview]:   { bg: '#FEF3C7', fg: '#92400E', label: 'In Review' },
  [TaskStatus.NotStarted]: { bg: '#F3F4F6', fg: '#5A6272', label: 'Not Started' },
  [TaskStatus.OnHold]:     { bg: '#FEE2E2', fg: '#991B1B', label: 'On Hold' },
  [TaskStatus.Cancelled]:  { bg: '#F3F4F6', fg: '#B8BEC9', label: 'Cancelled' },
};

// Task-type discriminator chips. Plain Task renders no chip (the default,
// overwhelmingly common case stays visually quiet). Colors reuse tokens
// already on the card: Story shares the subtasks-cell purple, Subtask the
// neutral pill grey — nothing outside the locked palette.
const TYPE_BADGE: Record<number, { bg: string; fg: string; label: string }> = {
  [TaskType.Story]:   { bg: '#F3E8FF', fg: '#7E22CE', label: 'Story' },
  [TaskType.Subtask]: { bg: '#F3F4F6', fg: '#5A6272', label: 'Subtask' },
};

const PILL_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'var(--font-ui)',
  fontSize: 11,
  fontWeight: 500,
  padding: '3px 6px',
  borderRadius: 4,
  whiteSpace: 'nowrap',
  textAlign: 'center',
};

interface TaskCardProps {
  task: Task;
  onClick: (task: Task) => void;
  pca?: IPublicClientApplication;
  isSubtask?: boolean;
  parentColor?: number | null;
  phaseName?: string;
  /** Called after the card's task is deleted so the parent can drop it. */
  onDeleted?: (taskId: string) => void;
}

function initials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Card due-date label. Shows month + day; adds the year only when the due
// date is outside the current calendar year so near-term dates stay compact.
function formatDueDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const opts: Intl.DateTimeFormatOptions = d.getFullYear() === now.getFullYear()
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' };
  return d.toLocaleDateString(undefined, opts);
}

// A task is "closed" (no longer overdue-flaggable) when Done or Cancelled.
function isClosedStatus(status: number): boolean {
  return status === TaskStatus.Done || status === TaskStatus.Cancelled;
}

function CalendarGlyph({ color }: { color: string }) {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden style={{ flexShrink: 0 }}>
      <rect x="1.5" y="2.5" width="9" height="8" rx="1.5" stroke={color} strokeWidth="1.1" />
      <path d="M1.5 4.8h9M3.6 1.4v2M8.4 1.4v2" stroke={color} strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  );
}

export default function TaskCard({ task, onClick, pca, phaseName, onDeleted }: TaskCardProps) {
  const myOid = getMyOid(pca);
  const [pin, setPin] = usePinState(myOid, task.dagrun_pm_taskid, pca);
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPunch, setShowPunch] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const projectId = task._dagrun_pm_projectid_value;
  const canPunch = !!pca && !!projectId;
  const canDelete = !!pca && !!onDeleted;
  // The kebab menu appears whenever any action is available.
  const hasMenu = canPunch || canDelete;

  const doDelete = async () => {
    if (!pca || deleting) return;
    setDeleting(true);
    try {
      await deleteTask(pca, task);
      setShowDelete(false);
      onDeleted?.(task.dagrun_pm_taskid);
    } catch {
      // Leave the card in place; the confirm modal closes so the user can retry.
      setShowDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const priorityToken = task.dagrun_pm_priority != null ? PRIORITY_COLOR[task.dagrun_pm_priority] : null;
  const leftBorderColor = priorityToken?.bg ?? NULL_BORDER;

  const status = STATUS_BADGE[task.dagrun_pm_status] ?? STATUS_BADGE[TaskStatus.NotStarted];

  const checklistTotal = task.dagrun_pm_checklistcount ?? 0;
  const checklistDone  = task.dagrun_pm_checklistcompletedcount ?? 0;

  const subtaskCount = task.dagrun_pm_subtaskcount ?? 0;
  const hasNote      = !!(task.dagrun_pm_description && task.dagrun_pm_description.trim().length > 0);

  const phaseTag = phaseName ?? '';
  const typeBadge = task.dagrun_pm_tasktype != null ? TYPE_BADGE[task.dagrun_pm_tasktype] : undefined;

  // Overdue = a due date in the past on a task that isn't Done/Cancelled.
  // Compared at day granularity so a task due today is not flagged overdue.
  const overdue = (() => {
    if (!task.dagrun_pm_duedate || isClosedStatus(task.dagrun_pm_status)) return false;
    const due = new Date(task.dagrun_pm_duedate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due.getTime() < today.getTime();
  })();

  return (
    <div
      onClick={() => onClick(task)}
      style={{
        position: 'relative',
        width: 240,
        // Without border-box, the 1px border ADDS to width — total
        // rendered = 242px which overflows the 240px column inner area
        // by 2px and triggers a horizontal scrollbar inside the
        // collection's vertical scroll container.
        boxSizing: 'border-box',
        background: '#FFFFFF',
        border: '1px solid #EAECF0',
        borderRadius: 7,
        marginBottom: 7,
        cursor: 'pointer',
        fontFamily: 'var(--font-ui)',
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'box-shadow 0.12s, border-color 0.12s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,116,144,0.12)';
        e.currentTarget.style.borderColor = '#b0d8e5';
        setHovered(true);
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = '#EAECF0';
        setHovered(false);
      }}
    >
      {/* Priority stripe — 3px left edge, locked priority color. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: 0, top: 0, bottom: 0,
          width: 3,
          background: leftBorderColor,
          pointerEvents: 'none',
        }}
      />

      {/* Card action menu — hover-revealed kebab. Offers "Create Punch Item"
          (opens a quick-create modal seeded with the task name) and, when the
          host view supplies onDeleted, "Delete task" (confirm-gated). */}
      {hasMenu && (
        <div
          onClick={e => e.stopPropagation()}
          onMouseDown={e => e.stopPropagation()}
          style={{
            position: 'absolute',
            // Sit left of the assignee avatar when present (avatar is 22px at right:10).
            top: 8, right: task.dagrun_pm_assigneeid ? 38 : 8,
            zIndex: 4,
          }}
        >
          <button
            type="button"
            aria-label="Card actions"
            onClick={e => { e.stopPropagation(); setMenuOpen(o => !o); }}
            style={{
              width: 20, height: 20, borderRadius: 4,
              border: 'none', background: menuOpen ? '#EAECF0' : 'transparent',
              color: '#5A6272', cursor: 'pointer',
              opacity: hovered || menuOpen ? 1 : 0,
              transition: 'opacity 0.12s',
              fontSize: 15, lineHeight: 1, padding: 0,
              fontFamily: 'var(--font-ui)',
            }}
          >⋮</button>
          {menuOpen && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, marginTop: 2,
              background: '#fff', border: '1px solid #DDE0E6', borderRadius: 8,
              boxShadow: '0 4px 16px rgba(15,17,23,0.12)', minWidth: 160, zIndex: 50, padding: 4,
            }}>
              {canPunch && (
                <div
                  onClick={e => { e.stopPropagation(); setMenuOpen(false); setShowPunch(true); }}
                  style={{ padding: '7px 10px', fontSize: 13, color: '#0F1117', cursor: 'pointer', borderRadius: 6 }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#F5F6F8')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >Create Punch Item</div>
              )}
              {canDelete && (
                <div
                  onClick={e => { e.stopPropagation(); setMenuOpen(false); setShowDelete(true); }}
                  style={{ padding: '7px 10px', fontSize: 13, color: '#DC2626', cursor: 'pointer', borderRadius: 6 }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#FEF2F2')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >Delete task</div>
              )}
            </div>
          )}
        </div>
      )}

      {showPunch && canPunch && pca && projectId && (
        <div onClick={e => e.stopPropagation()}>
          <QuickCreatePunchModal
            pca={pca}
            projectId={projectId}
            initialName={task.dagrun_pm_name}
            onClose={() => setShowPunch(false)}
            onCreated={() => setShowPunch(false)}
          />
        </div>
      )}

      {showDelete && canDelete && (
        <div onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
          <BoardModal
            open
            title="Delete task"
            onClose={() => !deleting && setShowDelete(false)}
            footer={<>
              <BMCancelButton onClick={() => setShowDelete(false)} disabled={deleting} />
              <BMDangerButton onClick={doDelete} disabled={deleting}>{deleting ? 'Deleting…' : 'Delete task'}</BMDangerButton>
            </>}
          >
            Delete <strong>{task.dagrun_pm_name}</strong>? This permanently removes the task
            {task.dagrun_pm_subtaskcount ? ' and its subtasks' : ''}. This cannot be undone.
          </BoardModal>
        </div>
      )}

      {/* Zone 1 — Header. Avatar absolute top-right, title 2-line wrap. */}
      <div style={{ position: 'relative', padding: '10px 12px 8px 14px' }}>
        {task.dagrun_pm_assigneeid && (
          <div
            title={task.dagrun_pm_assigneename ?? ''}
            style={{
              position: 'absolute',
              top: 10, right: 10,
              width: 22, height: 22, borderRadius: '50%',
              background: '#0E7490', color: '#FFFFFF',
              fontFamily: 'var(--font-ui)', fontSize: 10, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
              zIndex: 2,
            }}
          >{initials(task.dagrun_pm_assigneename)}</div>
        )}
        {typeBadge && (
          <div style={{ marginBottom: 4 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              padding: '1px 6px', borderRadius: 4,
              background: typeBadge.bg, color: typeBadge.fg,
            }}>{typeBadge.label}</span>
          </div>
        )}
        <div style={{
          fontSize: 13, fontWeight: 500, color: '#0F1117',
          lineHeight: 1.4,
          // 40px right reserves room for the absolute-positioned 22px
          // avatar at top-right (10px from edge + 22 + 8px buffer).
          // Always reserve the gutter even when no avatar is set so the
          // visual rhythm doesn't shift between assigned/unassigned.
          paddingRight: 40,
          // No line-clamp. No ellipsis. No nowrap. Title wraps to as
          // many lines as the content needs — card height grows with it.
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
        }}>
          {task.dagrun_pm_name}
        </div>
        {phaseTag && (
          <div style={{
            marginTop: 4,
            fontSize: 11,
            color: 'var(--ink-3)',
            fontFamily: 'var(--font-ui)',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}>{phaseTag}</div>
        )}
        {task.dagrun_pm_duedate && (
          <div
            title={overdue ? `Overdue — due ${formatDueDate(task.dagrun_pm_duedate)}` : `Due ${formatDueDate(task.dagrun_pm_duedate)}`}
            style={{
              marginTop: 5,
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 11, fontFamily: 'var(--font-ui)',
              color: overdue ? '#DC2626' : 'var(--ink-3)',
              fontWeight: overdue ? 600 : 400,
            }}
          >
            <CalendarGlyph color={overdue ? '#DC2626' : '#9198A8'} />
            {formatDueDate(task.dagrun_pm_duedate)}
          </div>
        )}
      </div>

      {/* Zone 2 — Status + Priority equal-width 2-col grid. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 4,
        padding: '0 12px 8px 14px',
      }}>
        <span style={{ ...PILL_STYLE, background: status.bg, color: status.fg }}>{status.label}</span>
        {priorityToken ? (
          <span style={{ ...PILL_STYLE, background: priorityToken.bg, color: priorityToken.fg }}>{priorityToken.label}</span>
        ) : (
          <span style={{ ...PILL_STYLE, background: '#F3F4F6', color: '#9198A8' }}>—</span>
        )}
      </div>

      {/* Pinned content (notes / checklist / subtasks) — render above footer when pinned. */}
      {pin.notes && pca && (
        <div style={{ padding: '0 12px 8px 14px' }}>
          <PinnedNote pca={pca} taskId={task.dagrun_pm_taskid} initial={task.dagrun_pm_description ?? ''} />
        </div>
      )}
      {pin.checklist && checklistTotal > 0 && pca && (
        <div style={{ padding: '0 12px 8px 14px' }}>
          <PinnedChecklist pca={pca} taskId={task.dagrun_pm_taskid} done={checklistDone} total={checklistTotal} />
        </div>
      )}
      {pin.subtasks && subtaskCount > 0 && pca && (
        <div style={{ padding: '0 12px 8px 14px' }}>
          <PinnedSubtasks pca={pca} taskId={task.dagrun_pm_taskid} />
        </div>
      )}

      {/* Zone 3 — Footer indicator grid. ALWAYS rendered, all 3 cells visible. */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          borderTop: '0.5px solid var(--border-tertiary, #EAECF0)',
        }}
      >
        <FooterCell
          kind="notes"
          hasContent={hasNote}
          label="Notes"
          pinned={pin.notes}
          onClick={() => setPin('notes', !pin.notes)}
          showDivider={false}
        />
        <FooterCell
          kind="checklist"
          hasContent={checklistTotal > 0}
          label={checklistTotal > 0 ? `${checklistDone}/${checklistTotal}` : 'Checklist'}
          pinned={pin.checklist}
          onClick={() => setPin('checklist', !pin.checklist)}
          showDivider
        />
        <FooterCell
          kind="subtasks"
          hasContent={subtaskCount > 0}
          label={subtaskCount > 0 ? String(subtaskCount) : 'Subtasks'}
          pinned={pin.subtasks}
          onClick={() => setPin('subtasks', !pin.subtasks)}
          showDivider
        />
      </div>
    </div>
  );
}

// Filled cell tints + icon-text colors per the locked spec.
const FOOTER_CELL_FILLED: Record<string, { bg: string; fg: string }> = {
  notes:     { bg: '#E0F2FE', fg: '#0369A1' },
  checklist: { bg: '#DCFCE7', fg: '#15803D' },
  subtasks:  { bg: '#F3E8FF', fg: '#7E22CE' },
};
const FOOTER_CELL_EMPTY = { bg: 'var(--color-background-secondary, #F5F6F8)', fg: 'var(--color-text-tertiary, #9198A8)' };

function NotesIcon({ color }: { color: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path d="M2 2.5h8M2 5h8M2 7.5h5" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}
function ChecklistIcon({ color }: { color: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
      <rect x="1.5" y="1.5" width="9" height="9" rx="1.5" stroke={color} strokeWidth="1.2" />
      <path d="M3.5 6.2 L5 7.7 L8.5 4" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}
function SubtasksIcon({ color }: { color: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path d="M3 1.5v6.2c0 .9.7 1.5 1.5 1.5H10" stroke={color} strokeWidth="1.2" strokeLinecap="round" fill="none" />
      <path d="M8 7.7 L10 9.2 L8 10.7" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

function FooterCell({ kind, hasContent, label, pinned, onClick, showDivider }: {
  kind: 'notes' | 'checklist' | 'subtasks';
  hasContent: boolean;
  label: string;
  pinned: boolean;
  onClick: () => void;
  showDivider: boolean;
}) {
  const filled = FOOTER_CELL_FILLED[kind];
  const bg = hasContent ? filled.bg : FOOTER_CELL_EMPTY.bg;
  const fg = hasContent ? filled.fg : FOOTER_CELL_EMPTY.fg;
  const Icon = kind === 'notes' ? NotesIcon : kind === 'checklist' ? ChecklistIcon : SubtasksIcon;
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${pinned ? 'Unpin' : 'Pin'} ${kind}`}
      style={{
        position: 'relative',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 3,
        padding: '7px 0',
        background: bg,
        color: fg,
        border: 'none',
        cursor: 'pointer',
        borderLeft: showDivider ? '0.5px solid rgba(255,255,255,0.5)' : 'none',
        outline: pinned ? '1px solid rgba(14,116,144,0.4)' : 'none',
        outlineOffset: -1,
        fontFamily: 'var(--font-ui)',
      }}
    >
      <Icon color={fg} />
      <span style={{ fontSize: 10, fontWeight: hasContent ? 500 : 400, color: fg, lineHeight: 1 }}>
        {label}
      </span>
    </button>
  );
}

// ── Pinned content ────────────────────────────────────────────────────
function PinnedNote({ pca, taskId, initial }: { pca: IPublicClientApplication; taskId: string; initial: string }) {
  const [value, setValue] = useState(initial);
  const debounce = useRef<number | null>(null);
  useEffect(() => { setValue(initial); }, [initial]);

  const commit = async (v: string) => {
    try { await dvUpdate(pca, 'dagrun_pm_tasks', taskId, { dagrun_pm_description: v }); } catch { /* best effort */ }
  };

  const schedule = (v: string) => {
    setValue(v);
    if (debounce.current) window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => commit(v), 700);
  };

  const flush = (v: string) => {
    if (debounce.current) { window.clearTimeout(debounce.current); debounce.current = null; }
    commit(v);
  };

  return (
    <div
      onClick={e => e.stopPropagation()}
      style={{
        padding: '6px 8px',
        background: '#F5F6F8',
        borderLeft: '2px solid #0E7490',
        borderRadius: 4,
        maxHeight: 200,
        overflowY: 'auto',
      }}
    >
      <AutoSizingTextarea
        value={value}
        onChange={schedule}
        onBlur={flush}
        placeholder="Add a note…"
        minHeight={40}
        rows={2}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          fontSize: 11,
          lineHeight: 1.45,
          color: '#2C313D',
          outline: 'none',
        }}
      />
    </div>
  );
}

function PinnedChecklist({ pca, taskId, done, total }: { pca: IPublicClientApplication; taskId: string; done: number; total: number }) {
  const [items, setItems] = useState<ChecklistItem[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    getChecklistItems(pca, taskId)
      .then(list => { if (!cancelled) setItems(list); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [pca, taskId]);

  const toggle = async (item: ChecklistItem) => {
    const next = !item.dagrun_pm_iscompleted;
    setItems(a => a ? a.map(x => x.dagrun_pm_checklistitemid === item.dagrun_pm_checklistitemid ? { ...x, dagrun_pm_iscompleted: next } : x) : a);
    try {
      await updateChecklistItem(pca, item.dagrun_pm_checklistitemid, {
        dagrun_pm_iscompleted: next,
        ...(next ? { dagrun_pm_completedon: new Date().toISOString() } : { dagrun_pm_completedon: null }),
      });
    } catch {
      setItems(a => a ? a.map(x => x.dagrun_pm_checklistitemid === item.dagrun_pm_checklistitemid ? { ...x, dagrun_pm_iscompleted: item.dagrun_pm_iscompleted } : x) : a);
    }
  };

  return (
    <div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#9198A8', marginBottom: 3 }}>
        {done} of {total} complete
      </div>
      {items === null ? (
        <div style={{ fontSize: 10, color: '#B8BEC9', fontStyle: 'italic' }}>Loading…</div>
      ) : (
        <div>
          {items.slice(0, 5).map(item => (
            <div key={item.dagrun_pm_checklistitemid}
              onClick={e => e.stopPropagation()}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '2px 0' }}
            >
              <input
                type="checkbox"
                checked={item.dagrun_pm_iscompleted}
                onChange={() => toggle(item)}
                style={{ width: 12, height: 12, cursor: 'pointer', flexShrink: 0 }}
              />
              <span style={{
                color: '#2C313D',
                textDecoration: item.dagrun_pm_iscompleted ? 'line-through' : 'none',
                opacity: item.dagrun_pm_iscompleted ? 0.6 : 1,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                flex: 1,
              }}>{item.dagrun_pm_name}</span>
            </div>
          ))}
          {items.length > 5 && (
            <div style={{ fontSize: 10, color: '#B8BEC9', marginTop: 3, fontStyle: 'italic' }}>
              +{items.length - 5} more — open task
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PinnedSubtasks({ pca, taskId }: { pca: IPublicClientApplication; taskId: string }) {
  const [items, setItems] = useState<Task[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    getSubtasks(pca, taskId)
      .then(list => { if (!cancelled) setItems(list); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [pca, taskId]);

  if (items === null) return <div style={{ fontSize: 10, color: '#B8BEC9', fontStyle: 'italic' }}>Loading…</div>;
  return (
    <div>
      {items.slice(0, 5).map(st => {
        const done = st.dagrun_pm_status === TaskStatus.Done;
        return (
          <div key={st.dagrun_pm_taskid} style={{
            fontSize: 11, padding: '2px 0',
            color: '#2C313D',
            textDecoration: done ? 'line-through' : 'none',
            opacity: done ? 0.6 : 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <span style={{ color: '#9198A8', fontSize: 9 }}>›</span>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{st.dagrun_pm_name}</span>
          </div>
        );
      })}
      {items.length > 5 && (
        <div style={{ fontSize: 10, color: '#B8BEC9', marginTop: 3, fontStyle: 'italic' }}>
          +{items.length - 5} more — open task
        </div>
      )}
      {items.length === 0 && (
        <div style={{ fontSize: 10, color: '#B8BEC9', fontStyle: 'italic' }}>No subtasks.</div>
      )}
    </div>
  );
}

export type { PinKey };
